import { OpenAPI } from '@/client';
import type {
  AccessLogGroupedRollupPublic,
  BlacklistDeviceStatePublic,
} from '@/client/types.gen';

export type BlackRecordWithUserStats = AccessLogGroupedRollupPublic & {
  used_days: number;
  current_consecutive_used_days: number;
  max_consecutive_used_days: number;
  browsing_comic_count: number;
  bookshelf_comic_count: number;
  search_count: number;
};

import { getToken } from '@/utils/token';
import type {
  AdminFcmMessagePayload,
  AdminFcmMessageResponse,
} from './fcmMessages';

export type Pagination<T> = {
  data: T[];
  count: number;
  skip?: number;
  page_size?: number;
};

export type WhitelistStrategy =
  | 'MISSING_REQUIRED_FIELDS'
  | 'SENSITIVE_REGION'
  | 'SENSITIVE_LANGUAGE'
  | 'VPN'
  | 'AUDIT_PERIOD'
  | 'INVALID_APP_VERSION'
  | 'TBA_CLOAK_2';

export type WhitelistRow = {
  device_id: string;
  enabled?: boolean | null;
  exempt_all?: boolean;
  policies: WhitelistStrategy[];
  show_ads?: boolean | null;
  show_zh_tags?: boolean | null;
  remark?: string | null;
  created_at?: string | null;
  updated_at?: string | null;
};

export type WhitelistRequest = {
  device_id?: string | null;
  enabled?: boolean | null;
  exempt_all?: boolean | null;
  policies?: WhitelistStrategy[];
  show_ads?: boolean | null;
  show_zh_tags?: boolean | null;
  remark?: string | null;
};

export type BrowsingComicItem = {
  id?: number | null;
  title: string;
  csid: string;
  source_site?: string | null;
  language_type?: string | null;
  supported_languages?: string[];
  tags?: BrowsingComicTag[];
  thumb_url?: string | null;
  hot_count?: number;
  priority?: number;
  read_chapter_no?: number | null;
  max_chapter_no?: number | null;
  first_viewed_at?: string | null;
};

export type BrowsingComicTag = {
  id: number;
  code: string;
  display_name: string;
};

export type ClientUserComicListSource = 'browsing' | 'bookshelf';

export type BSurfacePolicyEvent = {
  id: number;
  client_user_id?: number | null;
  client_id: string;
  device_id?: string | null;
  platform?: string | null;
  bundle_id?: string | null;
  version_code?: string | null;
  event_at: string;
  policy?: string | null;
  policy_trace?: string | null;
  reason: string;
  request_method?: string | null;
  request_path?: string | null;
  route_path?: string | null;
  ip_address?: string | null;
  country?: string | null;
  city?: string | null;
  subdivision?: string | null;
  system_language?: string | null;
  is_vpn?: boolean;
  source?: string | null;
  adp?: string | null;
  cloak?: string | null;
  headers?: Record<string, unknown>;
};

export type UserPreferenceTag = { tag_name: string };
export type AdminClientUserSettings = {
  id: number;
  preferred_cdn_host?: string | null;
  followed_tags: UserPreferenceTag[];
  blocked_tags: UserPreferenceTag[];
  available_tags: UserPreferenceTag[];
};

export type ClientUserSettingTag = {
  id: number;
  tag_name: string;
  enabled: boolean;
  sort_order: number;
  created_at: string;
  updated_at: string;
};

export type ClientUserSettingTagInput = {
  tag_name: string;
  enabled?: boolean;
  sort_order?: number;
};

type RequestOptions = RequestInit & {
  query?: Record<string, unknown>;
};

const buildUrl = (path: string, query?: Record<string, unknown>) => {
  const search = new URLSearchParams();
  Object.entries(query || {}).forEach(([key, value]) => {
    if (value === undefined || value === null || value === '') return;
    search.set(key, String(value));
  });
  return `${OpenAPI.BASE}${path}${search.toString() ? `?${search.toString()}` : ''}`;
};

const requestJson = async <T>(path: string, options: RequestOptions = {}) => {
  const token = getToken();
  const response = await fetch(buildUrl(path, options.query), {
    ...options,
    headers: {
      Accept: 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...(options.body ? { 'Content-Type': 'application/json' } : {}),
      ...(options.headers || {}),
    },
  });
  const payload = await response.json().catch(() => ({}));
  if (!response.ok) {
    throw {
      body: payload,
      message:
        (payload as { detail?: string; message?: string }).detail ||
        (payload as { detail?: string; message?: string }).message ||
        '请求失败',
    };
  }
  return payload as T;
};

const requestNoContent = async (path: string, options: RequestOptions = {}) => {
  const token = getToken();
  const response = await fetch(buildUrl(path, options.query), {
    ...options,
    headers: {
      Accept: 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...(options.body ? { 'Content-Type': 'application/json' } : {}),
      ...(options.headers || {}),
    },
  });
  if (!response.ok) {
    const payload = await response.json().catch(() => ({}));
    throw {
      body: payload,
      message:
        (payload as { detail?: string; message?: string }).detail ||
        (payload as { detail?: string; message?: string }).message ||
        '请求失败',
    };
  }
};

export const appUsersFacade = {
  listClientUsers(params: Record<string, unknown>) {
    return requestJson<Pagination<BlacklistDeviceStatePublic>>(
      '/api/v2/client-users',
      {
        query: params,
      },
    );
  },

  listBrowsingComics(
    clientId: string,
    params?: {
      source?: ClientUserComicListSource;
      platform?: string | null;
      bundle_id?: string | null;
      skip?: number;
      limit?: number;
      min_read_chapter_no?: number | null;
      max_read_chapter_no?: number | null;
    },
  ) {
    return requestJson<Pagination<BrowsingComicItem>>(
      `/api/v2/client-users/${encodeURIComponent(clientId)}/browsing-comics`,
      { query: params || {} },
    );
  },

  getClientUserSettings(params: {
    client_id: string;
    platform?: string | null;
    bundle_id?: string | null;
  }) {
    return requestJson<AdminClientUserSettings>(
      '/api/v2/client-users/settings',
      { query: params },
    );
  },

  listClientUserSettingTags(params?: {
    enabled?: boolean | null;
    keyword?: string | null;
  }) {
    return requestJson<{ items: ClientUserSettingTag[] }>(
      '/api/v2/client-user-setting-tags',
      { query: params || {} },
    );
  },

  createClientUserSettingTag(body: ClientUserSettingTagInput) {
    return requestJson<ClientUserSettingTag>(
      '/api/v2/client-user-setting-tags',
      {
        method: 'POST',
        body: JSON.stringify(body),
      },
    );
  },

  updateClientUserSettingTag(id: number, body: ClientUserSettingTagInput) {
    return requestJson<ClientUserSettingTag>(
      `/api/v2/client-user-setting-tags/${encodeURIComponent(id)}`,
      {
        method: 'PUT',
        body: JSON.stringify(body),
      },
    );
  },

  deleteClientUserSettingTag(id: number) {
    return requestNoContent(
      `/api/v2/client-user-setting-tags/${encodeURIComponent(id)}`,
      { method: 'DELETE' },
    );
  },

  listConfiguredCdnHosts() {
    return requestJson<
      Array<{
        host: string;
        provider: 'google_media_cdn' | 'cloudflare' | 'other';
        label: string;
      }>
    >('/api/v2/system/cdn-hosts');
  },

  listBSurfacePolicyEvents(
    clientId: string,
    params?: {
      platform?: string | null;
      bundle_id?: string | null;
      limit?: number;
    },
  ) {
    return requestJson<BSurfacePolicyEvent[]>(
      `/api/v2/client-users/${encodeURIComponent(clientId)}/b-surface-events`,
      { query: params || {} },
    );
  },

  updateClientUserSettings(body: {
    client_id: string;
    platform?: string | null;
    bundle_id?: string | null;
    show_zh_tags?: boolean;
    preferred_cdn_host?: string | null;
    followed_tags?: UserPreferenceTag[];
    blocked_tags?: UserPreferenceTag[];
  }) {
    return requestJson<AdminClientUserSettings>(
      '/api/v2/client-users/settings',
      {
        method: 'PATCH',
        body: JSON.stringify(body),
      },
    );
  },

  clearPolicyJudgment(body: {
    client_id: string;
    platform?: string | null;
    bundle_id?: string | null;
  }) {
    return requestJson<BlacklistDeviceStatePublic>(
      `/api/v2/client-users/${encodeURIComponent(body.client_id)}/policy-judgment/clear`,
      {
        method: 'POST',
        body: JSON.stringify(body),
      },
    );
  },

  sendFcmMessage(
    clientId: string,
    params: {
      platform?: string | null;
      bundle_id?: string | null;
    },
    payload: AdminFcmMessagePayload,
  ) {
    return requestJson<AdminFcmMessageResponse>(
      `/api/v2/client-users/${encodeURIComponent(clientId)}/fcm_message`,
      {
        method: 'POST',
        query: params,
        body: JSON.stringify(payload),
      },
    );
  },

  sendComicPushTest(
    params: {
      platform?: string | null;
      bundle_id?: string | null;
    },
    payload: {
      push_type: 'fixed_time' | 'comic_update';
      slot_key?: string;
      trigger_at?: string;
      client_ids: string[];
      csid?: string;
    },
  ) {
    return requestJson<{
      requested_client_ids: string[];
      matched_client_ids: string[];
      skipped_client_ids: string[];
      target_count: number;
      success_count: number;
      failed_count: number;
      errors: string[];
    }>('/api/v2/comic-push/test', {
      method: 'POST',
      query: params,
      body: JSON.stringify(payload),
    });
  },

  refreshLatestIpInfo(body: {
    client_id: string;
    platform?: string | null;
    bundle_id?: string | null;
  }) {
    return requestJson<BlacklistDeviceStatePublic>(
      '/api/v2/client-users/latest-ip-info',
      {
        method: 'POST',
        body: JSON.stringify(body),
      },
    );
  },

  lookupIpInfo(ip: string) {
    return requestJson<Record<string, unknown>>(
      '/api/v2/client-users/ip-info',
      {
        method: 'POST',
        body: JSON.stringify({ ip }),
      },
    );
  },

  listBlackRecords(params: Record<string, unknown>) {
    return requestJson<Pagination<BlackRecordWithUserStats>>(
      '/api/v2/black-records/grouped',
      {
        query: params,
      },
    );
  },

  listWhitelist(params: Record<string, unknown>) {
    return requestJson<Pagination<WhitelistRow>>('/api/v2/whitelist', {
      query: params,
    });
  },

  upsertWhitelist(body: WhitelistRequest) {
    return requestNoContent('/api/v2/whitelist', {
      method: 'POST',
      body: JSON.stringify(body),
    });
  },

  deleteWhitelist(body: WhitelistRequest) {
    return requestNoContent('/api/v2/whitelist', {
      method: 'DELETE',
      body: JSON.stringify(body),
    });
  },
};
